import React, { forwardRef, useImperativeHandle, useReducer, useState } from "react";
import PropTypes from "prop-types";
import isEmpty from "lodash-es/isEmpty";

// Material
import { makeStyles } from "@material-ui/core/styles";

// Config
import { defaultAccept } from "src/config/values";
import colors from "src/config/colors";

// Components
import FileInput from "src/components/Input/FileInput";
import Text from "src/components/Text";

const useStyles = makeStyles(theme => ({
  emptyFieldError: {
    marginBottom: theme.spacing(2),
    color: colors.rose
  }
}));

const types = {
  DROP: "DROP",
  REMOVE: "REMOVE",
  DESCRIPTION: "DESCRIPTION"
};

const reducer = (files, action) => {
  switch (action.type) {
    case types.DROP:
      return [
        ...files,
        ...action.files.map(file => ({ file, description: "" }))
      ];
    case types.DESCRIPTION: {
      const newFiles = [...files];
      newFiles[action.index] = {
        ...newFiles[action.index],
        description: action.description
      };
      return newFiles;
    }
    case types.REMOVE:
      return files.filter((_, index) => index !== action.index);
    default:
      return files;
  }
};

// No dialog, no own footer buttons - the caller (via ref) decides when to
// validate/submit the dropped files, e.g. a wizard's shared Next/Done button.
const InlineFileUpload = forwardRef(
  (
    { accept, multiple, noDescriptions, onSubmit, placeholderText, uploadErrors, uploadProgress },
    ref
  ) => {
    const classes = useStyles();
    const [files, dispatch] = useReducer(reducer, []);
    const [hasEmptyField, setHasEmptyField] = useState(false);

    const enableEdit = isEmpty(uploadProgress);
    const isUploading =
      !enableEdit &&
      Object.keys(uploadProgress).some(key => uploadProgress[key] < 100) &&
      !(
        uploadErrors && Object.keys(uploadErrors).every(key => uploadErrors[key])
      );

    useImperativeHandle(ref, () => ({
      submitPendingFiles: () => {
        if (files.length === 0) return "empty";

        const isInvalid =
          !noDescriptions && files.some(file => file.description === "");
        setHasEmptyField(isInvalid);
        if (isInvalid) return "invalid";

        onSubmit(files);
        return "submitted";
      }
    }));

    const handleDrop = addedFiles =>
      dispatch({ type: types.DROP, files: addedFiles });

    const handleRemoveItem = index => () =>
      dispatch({ type: types.REMOVE, index });

    const handleChangeDescription = ({ description, index }) =>
      dispatch({ type: types.DESCRIPTION, index, description });

    return (
      <>
        {hasEmptyField && (
          <Text
            className={classes.emptyFieldError}
            text="fileUploadPage.completeDescriptionsError"
            type="body"
          />
        )}
        <FileInput
          dropzoneProps={{ accept, multiple }}
          enableEdit={enableEdit}
          files={files}
          isUploading={isUploading}
          noDescriptions={noDescriptions}
          onChangeDescription={handleChangeDescription}
          onDrop={handleDrop}
          onRemoveItem={handleRemoveItem}
          placeholderHeight={325}
          placeholderText={placeholderText}
          uploadErrors={uploadErrors}
          uploadProgress={uploadProgress}
          width="100%"
        />
      </>
    );
  }
);

InlineFileUpload.propTypes = {
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  noDescriptions: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  placeholderText: PropTypes.string,
  uploadErrors: PropTypes.shape({}),
  uploadProgress: PropTypes.shape({})
};

InlineFileUpload.defaultProps = {
  accept: defaultAccept,
  multiple: false,
  noDescriptions: false,
  placeholderText: "fileUploadPage.dragDrop",
  uploadErrors: {},
  uploadProgress: {}
};

export default InlineFileUpload;
